import React, { useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { X } from 'lucide-react';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
  closeOnBackdrop?: boolean;
  children: React.ReactNode;
}

const sizeStyles: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({ open, onClose, title, size = 'md', footer, closeOnBackdrop = true, children }: ModalProps) {
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, handleEscape]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-label={title}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={closeOnBackdrop ? onClose : undefined}
      />

      {/* Panel */}
      <div className={cn(
        'relative w-full mx-4 bg-surface-raised/94 backdrop-blur-2xl rounded-[30px] shadow-modal border border-border',
        'animate-spring-in max-h-[85vh] flex flex-col',
        sizeStyles[size]
      )}>
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-5 border-b border-border flex-shrink-0">
            <div>
              <p className="apple-kicker">Dialog</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-[-0.04em] text-content">{title}</h2>
            </div>
            <button
              onClick={onClose}
              className="text-content-muted hover:text-content transition-colors rounded-full p-2 hover:bg-surface-overlay"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-border bg-surface-overlay/55 backdrop-blur-xl flex-shrink-0 flex items-center justify-end gap-3 rounded-b-[30px]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
